import {
  DD,
  DT,
  FormField,
  GridCol,
  GridRow,
  HelperText,
  Input,
  IntervalOption,
  isDefinedString,
  isValidDefinedDate,
  isValidDefinedInterval,
  isDefinedNumber,
  PercentageAdornment,
  Select,
  toIntervalString,
  toUTCDate,
  toZonedDateStringOrUndefined,
  Typography,
  useIntervalOption,
} from '@client/toolkit';
import { useTranslation } from 'react-i18next';
import { FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/nb';
import { DatepickerV2 } from '@client/toolkit';
import { FormValues, FormPath } from '../agreement-conditions-card.component';
import { IntervalType, IntervalValue, getApiBaseUrl, getApiAccessToken } from '@client/api';

type IndexType = 'Custom' | 'NorskKPI';

const INTERVAL_OPTIONS: IntervalOption[] = ['Monthly', 'EveryThreeMonth', 'Yearly'];
const INTERVAL_TYPE_OPTIONS: IntervalType[] = ['Month', 'Year'];

const INDEX_TYPE_OPTIONS: IndexType[] = ['NorskKPI', 'Custom'];

interface KpiState {
  loading: boolean;
  value: number | null;
  error: string | null;
  isAvailable: boolean;
}

interface KpiData {
  baseKpi: KpiState;
  currentKpi: KpiState;
  percentageChange: number | null;
}

interface IndexRegulationProps {
  isFetching: boolean;
  editing: boolean;
  setValue: UseFormSetValue<FormValues>;
  errors: FieldErrors<FormValues>;
  adjustmentPerInterval?: number | null;
  indexStartDate?: string | null;
  indexInterval?: { type?: IntervalType | null; count?: number | null } | null;
  indexType?: IndexType | null;
  isSubmitting: boolean;
  agreementStartDate?: string | null;
  baseIndexDate?: string | null;
  baseIndexValue?: number | null;
}

export const IndexRegulation = ({
  isFetching,
  editing,
  setValue,
  errors,
  adjustmentPerInterval,
  indexStartDate: indexStartDateString,
  indexInterval,
  indexType,
  isSubmitting,
  agreementStartDate,
  baseIndexDate: baseIndexDateProp,
  baseIndexValue: baseIndexValueProp,
}: IndexRegulationProps) => {
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const { t: tValidation } = useTranslation('validation');

  const [kpiData, setKpiData] = useState<KpiData>({
    baseKpi: { loading: false, value: null, error: null, isAvailable: true },
    currentKpi: { loading: false, value: null, error: null, isAvailable: true },
    percentageChange: null,
  });

  const watchedIndexType = useWatch({ name: 'index.type' }) as IndexType | undefined;
  const watchedStartDate = useWatch({ name: 'index.startDate' }) as string | undefined;
  const watchedBaseIndexDate = useWatch({ name: 'index.baseIndexDate' }) as string | undefined;
  
  const currentIndexType = editing ? (watchedIndexType ?? indexType ?? 'Custom') : (indexType ?? 'Custom');
  const currentStartDate = editing ? watchedStartDate : indexStartDateString;
  const currentBaseIndexDate = editing ? (watchedBaseIndexDate ?? baseIndexDateProp ?? agreementStartDate) : (baseIndexDateProp ?? agreementStartDate);

  const indexStartDate = useMemo(
    () =>
      indexStartDateString != null && isDefinedString(indexStartDateString)
        ? toUTCDate(indexStartDateString)
        : undefined,
    [indexStartDateString],
  );

  const fetchKpi = useCallback(async (year: number, month: number): Promise<KpiState> => {
    try {
      const apiUrl = getApiBaseUrl();
      const token = getApiAccessToken();
      const response = await fetch(`${apiUrl}/kpi?year=${year}&month=${month}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch KPI');
      }
      const data = await response.json();
      return {
        loading: false,
        value: data.value,
        error: null,
        isAvailable: data.isAvailable,
      };
    } catch (err) {
      return {
        loading: false,
        value: null,
        error: 'Kunne ikke hente KPI',
        isAvailable: false,
      };
    }
  }, []);

  useEffect(() => {
    const fetchBothKpi = async () => {
      if (currentIndexType !== 'NorskKPI') {
        setKpiData({
          baseKpi: { loading: false, value: null, error: null, isAvailable: true },
          currentKpi: { loading: false, value: null, error: null, isAvailable: true },
          percentageChange: null,
        });
        return;
      }

      const baseDate = currentBaseIndexDate ? dayjs(currentBaseIndexDate) : null;
      const adjustmentDate = currentStartDate ? dayjs(currentStartDate) : null;
      
      // For current KPI, use the month before the adjustment date
      const currentKpiDate = adjustmentDate?.subtract(1, 'month');

      setKpiData(prev => ({
        ...prev,
        baseKpi: { ...prev.baseKpi, loading: !!baseDate?.isValid() },
        currentKpi: { ...prev.currentKpi, loading: !!currentKpiDate?.isValid() },
      }));

      const [baseResult, currentResult] = await Promise.all([
        baseDate?.isValid() ? fetchKpi(baseDate.year(), baseDate.month() + 1) : Promise.resolve({ loading: false, value: null, error: null, isAvailable: true } as KpiState),
        currentKpiDate?.isValid() ? fetchKpi(currentKpiDate.year(), currentKpiDate.month() + 1) : Promise.resolve({ loading: false, value: null, error: null, isAvailable: true } as KpiState),
      ]);

      // Calculate percentage change
      let percentageChange: number | null = null;
      if (baseResult.value && currentResult.value && baseResult.value > 0) {
        percentageChange = ((currentResult.value - baseResult.value) / baseResult.value) * 100;
        percentageChange = Math.round(percentageChange * 100) / 100; // Round to 2 decimals
      }

      setKpiData({
        baseKpi: baseResult,
        currentKpi: currentResult,
        percentageChange,
      });

      // Update form values so they get saved to the database
      if (editing && baseResult.value) {
        setValue<FormPath>('index.baseIndexValue' as FormPath, baseResult.value, {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
      if (editing && percentageChange !== null) {
        setValue<FormPath>('index.adjustmentPerInterval', percentageChange, {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    };

    fetchBothKpi();
  }, [currentIndexType, currentBaseIndexDate, currentStartDate, fetchKpi, editing, setValue]);

  const { intervalOption: indexIntervalOption, updateIntervalOption: updateIndexIntervalOption } = useIntervalOption({
    interval: editing ? (indexInterval as IntervalValue) : undefined,
    onUpdateIntervalOption: ({ intervalCount, intervalType }) => {
      setValue<FormPath>('index.interval.count', intervalCount || null, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: true,
      });
      setValue<FormPath>('index.interval.type', intervalType || null, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: true,
      });
    },
  });

  const indexIntervalErrorMessage: string | undefined = useMemo(() => {
    if (errors?.index?.interval == null) return;
    return tValidation('field.required');
  }, [errors?.index?.interval, tValidation]);

  const hasIndexIntervalError = useMemo(
    () => !!indexIntervalErrorMessage && indexIntervalOption !== 'Custom',
    [indexIntervalErrorMessage, indexIntervalOption],
  );

  return (
    <div className="py-2" data-testid="index-regulation">
      <Typography variant="none" className="mb-4 text-brand-primary font-medium uppercase text-xs">
        {tGlobal('agreement.index')}
      </Typography>

      <GridRow cols={3} spacing={2} className="items-center">
        <GridCol span={1}>
          <DT>{tGlobal('agreement.indexType')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!indexType}>
              {indexType === 'NorskKPI' ? tGlobal('agreement.indexTypeNorskKpi') : tGlobal('agreement.indexTypeCustom')}
            </DD>
          ) : (
            <FormField<FormValues, FormPath>
              name={'index.type' as FormPath}
              render={({ field }) => (
                <Select size="medium" {...field} value={field.value ?? 'Custom'}>
                  {INDEX_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option === 'NorskKPI' ? tGlobal('agreement.indexTypeNorskKpi') : tGlobal('agreement.indexTypeCustom')}
                    </option>
                  ))}
                </Select>
              )}
            />
          )}
        </GridCol>

        {currentIndexType === 'NorskKPI' && (
          <>
            <GridCol span={1}>
              <DT>{tGlobal('agreement.indexBaseDate')}</DT>
            </GridCol>
            <GridCol span={2} loading={isFetching}>
              {!editing ? (
                <DD isEmpty={!currentBaseIndexDate}>
                  {currentBaseIndexDate ? dayjs(currentBaseIndexDate).locale('nb').format('MMMM YYYY') : null}
                </DD>
              ) : (
                <FormField<FormValues, FormPath>
                  name={'index.baseIndexDate' as FormPath}
                  render={({ field: { value, ...field } }) => (
                    <DatepickerV2
                      value={value ? dayjs(value) : (agreementStartDate ? dayjs(agreementStartDate) : undefined)}
                      {...field}
                      onChange={(date) => {
                        field.onChange(date?.format('YYYY-MM-DD'));
                      }}
                      className="w-full"
                      views={['month', 'year']}
                    />
                  )}
                />
              )}
            </GridCol>

            <GridCol span={1}>
              <DT>{tGlobal('agreement.indexBaseValue')}</DT>
            </GridCol>
            <GridCol span={2} loading={isFetching}>
              <DD isEmpty={false}>
                {kpiData.baseKpi.loading ? (
                  <span className="text-gray-400">{tGlobal('agreement.indexKpiLoading')}</span>
                ) : kpiData.baseKpi.error ? (
                  <span className="text-brand-primary font-normal">{tGlobal('agreement.indexKpiNotAvailable')}</span>
                ) : !currentBaseIndexDate ? (
                  <span className="text-gray-400">{tGlobal('agreement.indexKpiSelectDateFirst')}</span>
                ) : !kpiData.baseKpi.isAvailable ? (
                  <span className="text-brand-primary font-normal">{tGlobal('agreement.indexKpiNotAvailable')}</span>
                ) : kpiData.baseKpi.value ? (
                  <span>{kpiData.baseKpi.value} <span className="text-gray-400">({dayjs(currentBaseIndexDate).locale('nb').format('MMM YYYY')})</span></span>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </DD>
            </GridCol>
          </>
        )}

        <GridCol span={1}>
          <DT>{tGlobal('agreement.indexAdjustmentPerInterval')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {currentIndexType === 'NorskKPI' ? (
            <DD isEmpty={false}>
              {kpiData.baseKpi.loading || kpiData.currentKpi.loading ? (
                <span className="text-gray-400">{tGlobal('agreement.indexKpiLoading')}</span>
              ) : kpiData.baseKpi.error || kpiData.currentKpi.error ? (
                <span className="text-brand-primary font-normal">{tGlobal('agreement.indexKpiNotAvailable')}</span>
              ) : !currentBaseIndexDate || !currentStartDate ? (
                <span className="text-gray-400">{tGlobal('agreement.indexKpiSelectDateFirst')}</span>
              ) : !kpiData.baseKpi.isAvailable || !kpiData.currentKpi.isAvailable ? (
                <span className="text-brand-primary font-normal">{tGlobal('agreement.indexKpiNotAvailable')}</span>
              ) : kpiData.percentageChange !== null ? (
                <span>{kpiData.percentageChange} %</span>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </DD>
          ) : !editing ? (
            <DD isEmpty={!isDefinedNumber(adjustmentPerInterval)}>{adjustmentPerInterval} %</DD>
          ) : (
            <FormField<FormValues, FormPath>
              name="index.adjustmentPerInterval"
              render={({ field }) => (
                <PercentageAdornment size="small">
                  <Input type="number" className="pr-5" size="medium" {...field} />
                </PercentageAdornment>
              )}
            />
          )}
        </GridCol>

        <GridCol span={1}>
          <DT>{tGlobal('agreement.indexStartDate')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!isValidDefinedDate(indexStartDate)}>{toZonedDateStringOrUndefined(indexStartDate)}</DD>
          ) : (
            <FormField<FormValues, FormPath>
              name="index.startDate"
              render={({ field: { value, ...field } }) => (
                <DatepickerV2
                  value={value ? dayjs(value) : undefined}
                  {...field}
                  onChange={(date) => {
                    field.onChange(date?.format('YYYY-MM-DD'));
                  }}
                  className="w-full"
                />
              )}
            />
          )}
        </GridCol>

        <GridCol span={1}>
          <DT>{tGlobal('agreement.indexInterval')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!isValidDefinedInterval(indexInterval as any)}>{toIntervalString(indexInterval as any)}</DD>
          ) : (
            <>
              <Select
                size="medium"
                value={indexIntervalOption ?? ''}
                onChange={(e) => updateIndexIntervalOption(e.target.value as IntervalOption)}
                error={hasIndexIntervalError}
                submitting={isSubmitting}
              >
                <option />
                {INTERVAL_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {tType(`IntervalOption.${option}`)}
                  </option>
                ))}
              </Select>
              {hasIndexIntervalError && (
                <HelperText className="mt-1" error={hasIndexIntervalError}>
                  {indexIntervalErrorMessage}
                </HelperText>
              )}
            </>
          )}
        </GridCol>

        {editing && (
          <>
            {indexIntervalOption === 'Custom' && (
              <>
                <GridCol span={1}>
                  <DT>{tGlobal('agreement.indexIntervalType')}</DT>
                </GridCol>
                <GridCol span={2} loading={isFetching}>
                  <FormField<FormValues, FormPath>
                    name="index.interval.type"
                    render={({ field }) => (
                      <Select size="medium" {...field}>
                        <option value="" />
                        {INTERVAL_TYPE_OPTIONS.map((type) => (
                          <option key={type} value={type}>
                            {tType(`IntervalType.${type}`)}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </GridCol>

                <GridCol span={1}>
                  <DT>{tGlobal('agreement.indexIntervalCount')}</DT>
                </GridCol>
                <GridCol span={2} loading={isFetching}>
                  <FormField<FormValues, FormPath>
                    name="index.interval.count"
                    render={({ field }) => <Input type="number" size="small" {...field} />}
                  />
                </GridCol>
              </>
            )}
          </>
        )}
      </GridRow>
    </div>
  );
};
